import React from "react";
import { Link } from "react-router-dom";

// ─── Shared inline styles for article body ───────────────────────────────────
const H2 = ({ children }) => (
  <h2 className="text-2xl font-bold text-[#012d5c] mt-10 mb-4">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-xl font-semibold text-[#005eb8] mt-6 mb-3">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-5">{children}</p>
);
const UL = ({ items }) => (
  <ul className="space-y-2 mb-6 pl-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-gray-700">
        <span className="text-[#4caf50] mt-1 shrink-0">✓</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
const Callout = ({ children }) => (
  <div className="bg-[#f0f4f8] border-l-4 border-[#005eb8] p-5 rounded-r-lg mb-6">
    <p className="text-[#012d5c] font-medium text-sm leading-relaxed">{children}</p>
  </div>
);
const InternalLink = ({ to, children }) => (
  <Link to={to} className="text-[#005eb8] hover:text-[#4caf50] underline underline-offset-2 font-medium transition-colors">
    {children}
  </Link>
);

// ─── Blog posts ───────────────────────────────────────────────────────────────
export const blogPosts = [
  {
    slug: "medical-cannabis-for-chronic-pain-management",
    title: "Medical Cannabis for Chronic Pain Management",
    excerpt:
      "Chronic pain affects millions of people in the UK. We explore what the evidence says about medical cannabis as a treatment option and what patients should know.",
    category: "Research",
    date: "15 August 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1768498950637-88d073faa491?w=1200&auto=format&fit=crop&q=80",
    seoTitle: "Medical Cannabis for Chronic Pain | Integro Clinics",
    seoDescription:
      "Explore what the evidence says about medical cannabis for chronic pain management in the UK and how Integro Clinics can help eligible patients.",
    relatedSlugs: [
      "understanding-the-endocannabinoid-system",
      "medical-cannabis-vs-cbd-oil",
    ],
    sources: [
      { title: "NICE Guideline NG193 – Chronic pain (primary and secondary) in over 16s", url: "https://www.nice.org.uk/guidance/ng193" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
    ],
    Body: () => (
      <>
        <P>
          Chronic pain — broadly defined as pain lasting three months or more — is one of the most
          common reasons patients explore medical cannabis in the UK. It can stem from a wide range
          of underlying conditions, including{" "}
          <InternalLink to="/conditions/back-pain">back pain</InternalLink>,{" "}
          <InternalLink to="/conditions/fibromyalgia">fibromyalgia</InternalLink>,{" "}
          <InternalLink to="/conditions/arthritis">arthritis</InternalLink>, and{" "}
          <InternalLink to="/conditions/cancer-related-pain">cancer-related pain</InternalLink>.
          When conventional treatments — including over-the-counter medications, physiotherapy, and
          prescribed opioids — fail to provide adequate relief, patients and clinicians alike are
          increasingly looking at the evidence for cannabis-based medicines.
        </P>

        <H2>What does the evidence suggest?</H2>
        <P>
          The research landscape is evolving rapidly. A growing body of clinical studies and
          patient-reported outcomes suggests that cannabinoids — the active compounds in medical
          cannabis — may help reduce the intensity of chronic pain in certain patients, particularly
          where standard treatments have not been effective.
        </P>
        <P>
          The main cannabinoids studied for pain management are tetrahydrocannabinol (THC) and
          cannabidiol (CBD). THC appears to have analgesic (pain-relieving) properties, while CBD
          may contribute anti-inflammatory effects. In practice, medical cannabis products used for
          pain often combine both compounds in carefully calibrated ratios.
        </P>
        <P>
          It is important to note that medical cannabis is not a cure for chronic pain, and not
          every patient responds in the same way. Clinical trials have found it to be modestly
          effective for some patients, particularly those with neuropathic (nerve-related) pain, but
          the evidence base is still developing and individual responses can vary significantly.
        </P>

        <H2>Who might be considered for treatment?</H2>
        <P>
          In the UK, medical cannabis may be prescribed by specialist clinicians for chronic pain
          conditions where other treatments have not provided adequate relief. Eligibility is
          assessed on an individual basis and typically requires:
        </P>
        <UL
          items={[
            "A formal diagnosis of a chronic pain condition",
            "Evidence that at least two conventional treatments have been tried without sufficient benefit",
            "An assessment by a specialist clinician",
            "Review by a multi-disciplinary team (MDT) before any prescription is issued",
          ]}
        />
        <Callout>
          A prescription is not automatically issued following a consultation. At Integro, every
          prescribing decision is made following a thorough MDT review and clinical approval.
        </Callout>

        <H2>How does the treatment process work?</H2>
        <P>
          At Integro Clinics, the process begins with a short{" "}
          <InternalLink to="/#eligibility">eligibility check</InternalLink>. If you appear
          suitable, you will create a patient account, complete your medical profile, and attend an
          initial consultation with one of our specialist clinicians via a secure video call. Your
          case is then reviewed by our MDT before any decision on prescribing is made.
        </P>
        <P>
          If your prescription is approved, medication is dispensed by a regulated pharmacy and
          delivered discreetly to your home. Clinical consultations are provided at no charge.
          A £4.99 flat fee applies per prescription.
        </P>

        <H2>What should patients know before starting?</H2>
        <UL
          items={[
            "Medical cannabis is a prescription-only medicine in the UK — it cannot be bought over the counter",
            "Not all patients will be eligible, and not all who consult will receive a prescription",
            "It is important to disclose all current medications, as interactions are possible",
            "Effects and tolerability vary between individuals",
            "Regular follow-up reviews are part of responsible ongoing care",
          ]}
        />
        <P>
          If you are considering medical cannabis for chronic pain, the first step is to{" "}
          <InternalLink to="/#eligibility">check your eligibility</InternalLink> and speak with a
          specialist. Our team at Integro is here to guide you through the process with honesty and
          clinical expertise.
        </P>
        <P>
          You may also find our{" "}
          <InternalLink to="/faq">frequently asked questions</InternalLink> helpful, or read more
          about specific{" "}
          <InternalLink to="/conditions/fibromyalgia">pain conditions we consider</InternalLink>.
        </P>
      </>
    ),
  },

  {
    slug: "understanding-the-endocannabinoid-system",
    title: "Understanding the Endocannabinoid System",
    excerpt:
      "How does medical cannabis actually work? We explain the body's own endocannabinoid system and why it matters for understanding cannabis-based treatment.",
    category: "Education",
    date: "10 August 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1748969721837-2b290323022b?w=800&auto=format&fit=crop&q=80",
    seoTitle: "The Endocannabinoid System Explained | Integro Clinics",
    seoDescription:
      "Understand how the body's endocannabinoid system works and why it is central to understanding medical cannabis treatment in the UK.",
    relatedSlugs: [
      "medical-cannabis-vs-cbd-oil",
      "medical-cannabis-for-chronic-pain-management",
    ],
    sources: [
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
      { title: "National Institute on Drug Abuse – How does marijuana produce its effects?", url: "https://nida.nih.gov/publications/research-reports/marijuana/how-does-marijuana-produce-its-effects" },
    ],
    Body: () => (
      <>
        <P>
          When patients first hear about medical cannabis, a common question is: how does it
          actually work in the body? The answer lies in a biological system most people have never
          heard of — the endocannabinoid system (ECS). Understanding it helps explain why
          cannabinoids can influence such a wide range of conditions, from chronic pain to anxiety
          and sleep.
        </P>

        <H2>What is the endocannabinoid system?</H2>
        <P>
          The endocannabinoid system is a complex cell-signalling network found throughout the human
          body. It was not discovered until the early 1990s, when scientists studying the effects of
          cannabis on the brain identified specialised receptors that appeared to be designed to
          interact with cannabinoid compounds.
        </P>
        <P>
          The ECS is made up of three core components:
        </P>
        <UL
          items={[
            "Endocannabinoids — naturally occurring compounds produced by the body itself",
            "Receptors — proteins found on the surface of cells throughout the brain and body that endocannabinoids bind to",
            "Enzymes — which break down endocannabinoids once they have done their job",
          ]}
        />

        <H2>What does it do?</H2>
        <P>
          The ECS plays a regulatory role in a wide range of physiological processes. Current
          research suggests it is involved in:
        </P>
        <UL
          items={[
            "Pain perception and inflammation",
            "Mood regulation and stress response",
            "Sleep and circadian rhythms",
            "Appetite and metabolism",
            "Immune function",
            "Memory and learning",
          ]}
        />
        <P>
          This broad regulatory role is why disruptions to the ECS have been linked to a number of
          conditions, and why cannabinoids — which interact with this system — may have therapeutic
          potential across such a varied range of disorders.
        </P>

        <H2>How do cannabinoids interact with it?</H2>
        <P>
          The cannabis plant contains compounds called phytocannabinoids (plant-derived
          cannabinoids). The most well-known are THC (tetrahydrocannabinol) and CBD (cannabidiol).
          These compounds interact with the body's endocannabinoid receptors, primarily CB1 and CB2
          receptors.
        </P>
        <P>
          THC binds directly to CB1 receptors, which are found predominantly in the brain and
          central nervous system. This interaction is responsible for THC's psychoactive effects as
          well as many of its therapeutic properties, including pain relief and appetite stimulation.
        </P>
        <P>
          CBD interacts with the ECS in a more indirect way and does not produce psychoactive
          effects. It appears to modulate the activity of CB receptors and influence other receptor
          systems associated with pain and inflammation.
        </P>

        <H2>Why does this matter for medical cannabis treatment?</H2>
        <P>
          Understanding the ECS helps clinicians think about which cannabinoid formulations and
          ratios might be most appropriate for a given condition or patient. It also explains why
          the effects of medical cannabis can vary so significantly between individuals — because
          the ECS itself varies from person to person in density, distribution, and sensitivity.
        </P>
        <Callout>
          Medical cannabis is not a one-size-fits-all treatment. At Integro, prescribing decisions
          are made on an individual basis following a specialist consultation and MDT review.
        </Callout>
        <P>
          If you would like to learn more about whether medical cannabis might be appropriate for
          your condition, visit our{" "}
          <InternalLink to="/#eligibility">eligibility section</InternalLink> or browse{" "}
          <InternalLink to="/conditions/back-pain">conditions we consider</InternalLink>. You can
          also find detailed answers in our{" "}
          <InternalLink to="/faq">FAQ page</InternalLink>.
        </P>
      </>
    ),
  },

  {
    slug: "medical-cannabis-vs-cbd-oil",
    title: "Medical Cannabis vs. CBD Oil: What's the Difference?",
    excerpt:
      "CBD oil is widely available in health shops, but it is not the same as prescribed medical cannabis. We explain the key differences clearly.",
    category: "Education",
    date: "5 August 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1758691461935-202e2ef6b69f?w=800&auto=format&fit=crop&q=80",
    seoTitle: "Medical Cannabis vs CBD Oil: Key Differences | Integro Clinics",
    seoDescription:
      "Confused about the difference between CBD oil and medical cannabis? We explain what each is, how they differ legally and clinically, and who can access medical cannabis in the UK.",
    relatedSlugs: [
      "understanding-the-endocannabinoid-system",
      "uk-medical-cannabis-law-2025",
    ],
    sources: [
      { title: "Food Standards Agency – Cannabidiol (CBD)", url: "https://www.food.gov.uk/business-guidance/cannabidiol-cbd" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
    ],
    Body: () => (
      <>
        <P>
          Walk into any health food shop in the UK and you will likely find a shelf dedicated to CBD
          products — oils, capsules, gummies, creams. Meanwhile, medical cannabis remains a
          prescription-only medicine that can only be issued by a specialist clinician. Understanding
          the difference between the two is important for any patient considering their options.
        </P>

        <H2>What is CBD oil?</H2>
        <P>
          CBD (cannabidiol) is one of many naturally occurring compounds in the cannabis plant. CBD
          oils and supplements sold in UK shops are derived from hemp plants and contain only trace
          amounts of THC — the compound responsible for psychoactive effects.
        </P>
        <P>
          In the UK, CBD products are legally sold as food supplements, provided they contain less
          than 0.2% THC. This means they are not regulated by the Medicines and Healthcare products
          Regulatory Agency (MHRA) as medicines. Manufacturers cannot make specific health or
          medical claims about their products.
        </P>
        <H3>What can CBD oil do?</H3>
        <P>
          Some people report finding CBD supplements helpful for general wellness purposes such as
          stress, mild discomfort, or improving sleep quality. However, because CBD supplements are
          not licensed medicines, the quality, purity, and concentration of products can vary
          considerably, and the evidence base for many of these uses remains limited.
        </P>

        <H2>What is medical cannabis?</H2>
        <P>
          Medical cannabis refers to cannabis-based products that are prescribed by a licensed
          specialist clinician as a regulated medicine. In the UK, medical cannabis was legalised for
          prescription by specialist doctors in November 2018.
        </P>
        <P>
          Unlike over-the-counter CBD supplements, prescribed medical cannabis:
        </P>
        <UL
          items={[
            "Is a regulated medicine produced to pharmaceutical-grade standards",
            "May contain significant levels of THC alongside CBD",
            "Can only be prescribed by a GMC-registered specialist consultant",
            "Is subject to the same clinical governance as any other prescription medicine",
            "Requires formal assessment of a patient's medical history and condition",
            "Is dispensed by a licensed pharmacy and delivered to the patient",
          ]}
        />

        <H2>Key differences at a glance</H2>
        <UL
          items={[
            "CBD supplements: available over the counter; no prescription required; regulated as food supplement; limited THC",
            "Medical cannabis: prescription only; specialist clinician required; regulated medicine; may contain THC; subject to MDT review",
          ]}
        />

        <Callout>
          If you have tried CBD supplements without adequate benefit, and have a diagnosed medical
          condition, you may wish to explore whether prescribed medical cannabis is appropriate for
          you. The first step is to check your eligibility.
        </Callout>

        <H2>How do I access medical cannabis in the UK?</H2>
        <P>
          To access prescribed medical cannabis in the UK, you need a referral to or consultation
          with a specialist clinician. You do not need a GP referral — clinics like Integro accept
          self-referrals via our short eligibility checker. However, you will need to provide
          relevant medical history and documentation.
        </P>
        <P>
          Our specialist team will assess whether you may be suitable for treatment. If appropriate,
          your case is reviewed by our multi-disciplinary team (MDT) before any prescription is
          issued.
        </P>
        <P>
          To find out more, visit our{" "}
          <InternalLink to="/faq">FAQ page</InternalLink>, read about the{" "}
          <InternalLink to="/conditions/back-pain">conditions we consider</InternalLink>, or{" "}
          <InternalLink to="/#eligibility">check your eligibility</InternalLink> today.
        </P>
      </>
    ),
  },

  {
    slug: "fibromyalgia-and-medical-cannabis",
    title: "Fibromyalgia and Medical Cannabis: What Patients Need to Know",
    excerpt:
      "Fibromyalgia can be difficult to treat with conventional medication. We look at the evidence for medical cannabis and what patients considering this option should understand.",
    category: "Conditions",
    date: "28 July 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1758691463393-a2aa9900af8a?w=800&auto=format&fit=crop&q=80",
    seoTitle: "Fibromyalgia and Medical Cannabis UK | Integro Clinics",
    seoDescription:
      "Could medical cannabis help with fibromyalgia? We explain what fibromyalgia is, what the evidence shows, and how eligible patients can explore treatment options in the UK.",
    relatedSlugs: [
      "medical-cannabis-for-chronic-pain-management",
      "understanding-the-endocannabinoid-system",
    ],
    sources: [
      { title: "NHS – Fibromyalgia", url: "https://www.nhs.uk/conditions/fibromyalgia/" },
      { title: "NICE Clinical Knowledge Summary – Fibromyalgia", url: "https://cks.nice.org.uk/topics/fibromyalgia/" },
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
    ],
    Body: () => (
      <>
        <P>
          Fibromyalgia is a chronic condition characterised by widespread musculoskeletal pain,
          fatigue, sleep difficulties, and cognitive challenges — sometimes referred to as
          'fibro-fog'. It is estimated to affect around 2% of the UK population, with women more
          commonly diagnosed than men. Despite its prevalence, fibromyalgia remains one of the more
          complex conditions to manage, partly because it does not respond consistently to
          conventional pain medication.
        </P>

        <H2>Why can fibromyalgia be difficult to treat?</H2>
        <P>
          Fibromyalgia does not involve inflammation or structural tissue damage in the way that
          conditions such as arthritis do. Instead, it is understood to involve central
          sensitisation — a process in which the central nervous system becomes hypersensitive to
          pain signals. This means the standard anti-inflammatory medications that work for many
          types of pain are often ineffective.
        </P>
        <P>
          Treatments typically prescribed for fibromyalgia include antidepressants, anticonvulsants,
          and pain modulators. While these help some patients, many find that the relief is
          insufficient or accompanied by side effects that are difficult to tolerate.
        </P>

        <H2>What does the evidence say about medical cannabis and fibromyalgia?</H2>
        <P>
          A growing body of research and clinical experience suggests that cannabinoids may help
          some patients with fibromyalgia, particularly with pain, sleep quality, and quality of
          life. Several observational studies have reported meaningful improvements in symptom
          scores among fibromyalgia patients using cannabis-based medicines.
        </P>
        <P>
          The proposed mechanism is that cannabinoids interact with the endocannabinoid system,
          which plays a role in pain modulation, mood, and sleep — all of which are disrupted in
          fibromyalgia. Some researchers have suggested that fibromyalgia may partly involve
          endocannabinoid system dysregulation, though this remains an active area of study.
        </P>
        <Callout>
          Medical cannabis is not a cure for fibromyalgia, and clinical responses vary. Not all
          patients who consult will be suitable for treatment, and a prescription is only issued
          following specialist assessment and MDT review.
        </Callout>

        <H2>Who might be considered for treatment?</H2>
        <P>
          Patients with a confirmed diagnosis of{" "}
          <InternalLink to="/conditions/fibromyalgia">fibromyalgia</InternalLink> who have tried at
          least two conventional treatment approaches without adequate benefit may be considered for
          medical cannabis treatment. Other eligibility criteria apply, and each case is assessed
          individually.
        </P>
        <UL
          items={[
            "Confirmed fibromyalgia diagnosis",
            "Previous trial of at least two conventional treatments",
            "Aged 21 or over",
            "No contraindications identified during clinical assessment",
          ]}
        />

        <H2>Symptoms commonly reported by fibromyalgia patients</H2>
        <UL
          items={[
            "Widespread musculoskeletal pain",
            "Sleep disturbance",
            "Fatigue",
            "Anxiety associated with chronic illness",
            "Overall quality of life",
          ]}
        />
        <P>
          If you have fibromyalgia and are wondering whether you might be suitable for treatment,
          the best first step is to{" "}
          <InternalLink to="/#eligibility">check your eligibility</InternalLink>. You may also
          find our <InternalLink to="/faq">FAQ page</InternalLink> useful, or read more about{" "}
          <InternalLink to="/conditions/fibromyalgia">fibromyalgia on our conditions page</InternalLink>.
        </P>
      </>
    ),
  },

  {
    slug: "sleep-and-medical-cannabis",
    title: "Sleep and Medical Cannabis: What Patients Should Know",
    excerpt:
      "Poor sleep is a significant issue for many patients living with chronic conditions. We look at what the research shows about cannabinoids and sleep, and what to consider.",
    category: "Education",
    date: "20 July 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1723649379227-498e2ebf25c6?w=800&auto=format&fit=crop&q=80",
    seoTitle: "Medical Cannabis and Sleep: What the Evidence Shows | Integro Clinics",
    seoDescription:
      "Many patients with chronic conditions experience disrupted sleep. Find out what the evidence shows about medical cannabis and sleep, and what patients should consider.",
    relatedSlugs: [
      "understanding-the-endocannabinoid-system",
      "fibromyalgia-and-medical-cannabis",
    ],
    sources: [
      { title: "NHS – Insomnia", url: "https://www.nhs.uk/conditions/insomnia/" },
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
    ],
    Body: () => (
      <>
        <P>
          Disrupted sleep is one of the most common complaints among patients living with chronic
          conditions. Whether it is pain that prevents comfortable rest, anxiety that keeps the mind
          active at night, or the side effects of existing medications, poor sleep can compound the
          impact of chronic illness significantly.
        </P>
        <P>
          It is therefore unsurprising that many patients exploring medical cannabis ask about its
          potential effects on sleep — and that sleep improvement is frequently reported as one of
          the patient-noted benefits during treatment for other conditions.
        </P>

        <H2>How does the endocannabinoid system relate to sleep?</H2>
        <P>
          The body's{" "}
          <InternalLink to="/blog/understanding-the-endocannabinoid-system">
            endocannabinoid system (ECS)
          </InternalLink>{" "}
          plays a regulatory role in sleep-wake cycles. Research has identified endocannabinoid
          receptors in brain regions involved in circadian rhythm and sleep regulation. Animal
          studies and preliminary human research suggest that ECS signalling influences the amount
          of time spent in different sleep stages.
        </P>

        <H2>What does the evidence show about cannabinoids and sleep?</H2>
        <P>
          Research into cannabinoids and sleep is still developing, but some consistent findings
          are emerging:
        </P>
        <UL
          items={[
            "THC may reduce the time it takes to fall asleep in some individuals",
            "CBD has been associated with reduced anxiety in some studies, which may in turn support better sleep",
            "Some patients report more restful sleep and reduced night-waking during medical cannabis treatment",
            "Long-term effects on sleep architecture (the stages of sleep) are less well understood",
            "Individual responses vary considerably",
          ]}
        />
        <P>
          Importantly, patients often report sleep improvements as a secondary benefit of treating
          an underlying condition such as chronic pain or PTSD with medical cannabis, rather than
          targeting sleep directly.
        </P>

        <H2>What should patients be aware of?</H2>
        <Callout>
          Medical cannabis is not prescribed specifically for insomnia or sleep difficulties in
          isolation at Integro. It is assessed as a treatment for diagnosed medical conditions
          where other treatments have been inadequate.
        </Callout>
        <P>
          If sleep disruption is a significant feature of your condition — for example, as part of
          chronic pain, fibromyalgia, PTSD, or another qualifying condition — this will be
          discussed during your clinical assessment.
        </P>
        <UL
          items={[
            "Always disclose existing sleep medications and supplements during your consultation",
            "Cannabis can interact with other central nervous system medications",
            "Some patients find that THC-dominant products affect their dream recall or sleep stage distribution",
            "CBN (cannabinol) is another cannabinoid that has attracted interest for sleep, though the evidence base is limited",
          ]}
        />

        <H2>Next steps</H2>
        <P>
          If you are living with a chronic condition and disrupted sleep is part of your experience,
          start by checking whether you may be eligible for treatment at Integro. Our clinical team
          can help you understand whether medical cannabis might be an appropriate option as part of
          a broader treatment plan.
        </P>
        <P>
          Visit our <InternalLink to="/faq">FAQ page</InternalLink> or{" "}
          <InternalLink to="/#eligibility">check your eligibility</InternalLink> to get started.
          You can also read more about conditions such as{" "}
          <InternalLink to="/conditions/fibromyalgia">fibromyalgia</InternalLink> and{" "}
          <InternalLink to="/conditions/sleep-insomnia">Sleep &amp; Insomnia</InternalLink> on our conditions pages.
        </P>
      </>
    ),
  },

  {
    slug: "uk-medical-cannabis-law-2025",
    title: "UK Medical Cannabis Law: What Patients Need to Know",
    excerpt:
      "Medical cannabis has been legal in the UK since 2018, but many patients are still unsure about their rights and the access process. We explain the current legal landscape.",
    category: "Legal",
    date: "15 July 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1758691463582-11aea602cd4a?w=800&auto=format&fit=crop&q=80",
    seoTitle: "UK Medical Cannabis Law 2025: A Patient Guide | Integro Clinics",
    seoDescription:
      "Medical cannabis has been legal in the UK since 2018. Find out what the law says, who can prescribe, and how to access it legally through a licensed UK clinic.",
    relatedSlugs: [
      "medical-cannabis-vs-cbd-oil",
      "how-to-talk-to-your-doctor-about-medical-cannabis",
    ],
    sources: [
      { title: "Home Office – Drug licensing factsheet: cannabis, CBD and other cannabinoids", url: "https://www.gov.uk/government/publications/drug-licensing-factsheet-cannabis-cbd-and-other-cannabinoids" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
      { title: "Misuse of Drugs Regulations 2001 – UK Legislation", url: "https://www.legislation.gov.uk/uksi/2001/3998/contents" },
    ],
    Body: () => (
      <>
        <P>
          Despite medical cannabis having been legal for prescription in the United Kingdom since
          November 2018, a significant number of patients — and even healthcare professionals —
          remain uncertain about what the law actually permits, who can prescribe, and how the
          access process works in practice.
        </P>

        <H2>When did medical cannabis become legal in the UK?</H2>
        <P>
          In November 2018, following high-profile cases involving epileptic children, the UK
          government rescheduled cannabis-based medicinal products (CBMPs) from Schedule 1 to
          Schedule 2 under the Misuse of Drugs Regulations 2001. This change meant that specialist
          doctors could legally prescribe cannabis-based medicines where clinically appropriate.
        </P>
        <P>
          However, the change did not mean that cannabis was universally available on the NHS. In
          practice, NHS funding for medical cannabis prescriptions remains limited, and the majority
          of patients in the UK who access it do so through private specialist clinics.
        </P>

        <H2>Who can prescribe medical cannabis in the UK?</H2>
        <P>
          Medical cannabis in the UK can only be prescribed by a specialist consultant who is
          registered with the General Medical Council (GMC) and on the Specialist Register. General
          Practitioners (GPs) cannot currently prescribe cannabis-based medicines, though they may
          support a patient's referral to a specialist.
        </P>
        <Callout>
          You do not need a GP referral to access a private specialist clinic like Integro. You can
          apply directly via our eligibility checker. However, you will need to provide medical
          history and documentation.
        </Callout>

        <H2>What can be prescribed?</H2>
        <P>
          Clinicians may prescribe cannabis-based medicines for conditions where there is clinical
          evidence of benefit and where other treatments have not been adequate. Common areas of
          prescribing include chronic pain, neurological conditions, and certain psychiatric and
          neurodevelopmental conditions.
        </P>
        <P>
          Prescribing decisions at private clinics such as Integro are made following a thorough
          clinical assessment and multi-disciplinary team (MDT) review. A prescription is not
          guaranteed following a consultation.
        </P>

        <H2>Is medical cannabis the same as recreational cannabis?</H2>
        <P>
          No. Prescribed medical cannabis is a regulated pharmaceutical product, produced under
          controlled conditions to consistent quality and safety standards. It is dispensed by a
          licensed pharmacy and subject to the full range of clinical oversight associated with any
          prescription medicine.
        </P>
        <P>
          Recreational cannabis use remains illegal in the UK. It is important that patients access
          medical cannabis only through legal, licensed channels.
        </P>

        <H2>What are my rights as a patient?</H2>
        <UL
          items={[
            "You have the right to seek a specialist opinion on medical cannabis treatment",
            "You do not require a GP referral for a private specialist clinic",
            "Your prescription remains a private medical matter between you and your clinical team",
            "You may carry legally prescribed medical cannabis in the UK under your prescription",
            "Travel outside the UK with prescribed cannabis-based medicines may require additional documentation — always check the rules of your destination country",
          ]}
        />
        <P>
          For more information about how our process works, visit our{" "}
          <InternalLink to="/faq">FAQ page</InternalLink> or{" "}
          <InternalLink to="/#eligibility">check your eligibility</InternalLink> to start the
          process.
        </P>
      </>
    ),
  },

  {
    slug: "how-to-talk-to-your-doctor-about-medical-cannabis",
    title: "How to Talk to Your Doctor About Medical Cannabis",
    excerpt:
      "Many patients feel uncertain about raising medical cannabis with their GP. We share practical advice on how to approach the conversation and explore your options.",
    category: "Guide",
    date: "10 July 2025",
    author: "Integro Clinics",
    image:
      "https://images.unsplash.com/photo-1589279003513-467d320f47eb?w=800&auto=format&fit=crop&q=80",
    seoTitle: "How to Talk to Your Doctor About Medical Cannabis | Integro Clinics",
    seoDescription:
      "Thinking about raising medical cannabis with your GP? We share practical guidance on how to approach the conversation and understand your options as a UK patient.",
    relatedSlugs: [
      "uk-medical-cannabis-law-2025",
      "medical-cannabis-vs-cbd-oil",
    ],
    sources: [
      { title: "NHS – Medical cannabis (and cannabis oils)", url: "https://www.nhs.uk/conditions/medical-cannabis/" },
      { title: "GMC – Good medical practice", url: "https://www.gmc-uk.org/ethical-guidance/ethical-guidance-for-doctors/good-medical-practice" },
      { title: "MHRA – Cannabis-based products for medicinal use (CBPMs)", url: "https://www.gov.uk/guidance/cannabis-based-products-for-medicinal-use-cbpms" },
    ],
    Body: () => (
      <>
        <P>
          Raising the topic of medical cannabis with a GP can feel daunting. Some patients worry
          about being judged, about how their request will be received, or simply about not knowing
          enough to make a confident case. The good news is that the conversation does not need to
          be difficult — and you have every right to explore all legally available treatment options
          for your condition.
        </P>

        <H2>Understanding your GP's position</H2>
        <P>
          Your GP plays a central role in your care, but it is important to understand what they
          can and cannot do regarding medical cannabis. In the UK, GPs are currently not permitted
          to prescribe cannabis-based medicines themselves. Only specialist consultants on the GMC's
          Specialist Register can do so.
        </P>
        <P>
          This means that even if your GP is supportive, they cannot issue a medical cannabis
          prescription directly. What they can do is provide relevant medical history, write
          supporting letters, and in some cases provide a referral — though a referral is not
          required for private specialist clinics.
        </P>

        <H2>How to prepare for the conversation</H2>
        <P>
          Going into the appointment prepared makes a significant difference. Consider the
          following:
        </P>
        <UL
          items={[
            "Have a clear summary of your condition, its duration, and how it affects your daily life",
            "List the treatments you have already tried and why they were inadequate (side effects, insufficient relief, or both)",
            "Frame the conversation clinically — focus on your symptoms and the limitations of existing treatment",
            "Be honest about your situation; your GP is there to help you",
            "Ask specifically whether your GP can provide a summary of your medical history to support a specialist referral",
          ]}
        />

        <H2>What if my GP is not familiar with medical cannabis?</H2>
        <P>
          Awareness of medical cannabis among GPs varies considerably. If your GP is unfamiliar
          with the current legal framework, it can help to mention that:
        </P>
        <UL
          items={[
            "Medical cannabis was rescheduled in the UK in November 2018",
            "It can only be prescribed by a specialist consultant",
            "You would like support accessing a specialist assessment",
            "Private clinics such as Integro accept self-referrals",
          ]}
        />

        <H2>What if my GP is not supportive?</H2>
        <Callout>
          You do not need your GP's permission or a formal referral to access a private medical
          cannabis clinic. You can apply directly to Integro Clinics via our eligibility checker.
        </Callout>
        <P>
          Your medical history will be requested as part of the application process, and access to
          your GP records is normally required for a thorough clinical assessment. However, the
          decision to explore specialist treatment options is yours to make.
        </P>

        <H2>Before your specialist consultation</H2>
        <P>
          Whether or not your GP is involved, there are practical steps you can take to prepare for
          a specialist consultation:
        </P>
        <UL
          items={[
            "Gather any relevant letters, test results, or clinic correspondence",
            "Make a list of all current medications",
            "Note any relevant medical history including previous conditions",
            "Prepare questions you want answered about medical cannabis and your condition",
          ]}
        />
        <P>
          To find out whether you may be suitable for treatment at Integro, start by completing our{" "}
          <InternalLink to="/#eligibility">eligibility check</InternalLink>. It takes less than 30
          seconds. You may also find our{" "}
          <InternalLink to="/faq">FAQ page</InternalLink> and our{" "}
          <InternalLink to="/transfer-clinic">Transfer Clinic page</InternalLink> useful if you are
          already under the care of another clinic.
        </P>
      </>
    ),
  },
];

export default blogPosts;
